/*
 * File: ToolTip.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 1:03 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Constants
import { UI_MODE_OPTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './ToolTip.styles';


/**
 * Component
 */

export const ToolTip = ({
	place, type, effect, size, weight, ...rest
}) => {

	// Create state handlers
	const [isMounted, setIsMounted] = useState(false);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Handle actions on component load
	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Handle tooltip rendering
	const renderTooltip = (dataTip) => {
		if (type === 'success') {
			return (
				<S.Inner size={size} weight={weight}>
					<FontAwesomeIcon className="success" icon={['fal', 'check-circle']} />
					{dataTip}
				</S.Inner>
			);
		} if (type === 'error') {
			return (
				<S.Inner size={size} weight={weight}>
					<FontAwesomeIcon className="error" icon={['fal', 'circle-xmark']} />
					{dataTip}
				</S.Inner>
			);
		}
		return <S.Inner size={size} weight={weight}>{dataTip}</S.Inner>;
	};

	// Render component
	return (
		isMounted ? (
			<S.ToolTip
				uiMode={uiMode?.mode || UI_MODE_OPTIONS.LIGHT}
				place={place}
				type={type === 'default' ? 'light' : type}
				effect={effect}
				getContent={(dataTip) => renderTooltip(dataTip)}
				overridePosition={({ left, top }, _e, _t, node) => ({
					top,
					left: typeof node === 'string' ? left : Math.max(left, 16),
				})}
				{...rest}
			/>
		) : null
	);
};


/**
 * Configuration
 */

ToolTip.displayName = 'ToolTip';
ToolTip.propTypes = {
	place: PropTypes.string,
	type: PropTypes.oneOf(['default', 'success', 'error']),
	effect: PropTypes.oneOf(['solid', 'float']),
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	weight: PropTypes.oneOf(['extralight', 'light', 'regular', 'medium', 'semibold', 'bold', 'extrabold', 'black']),
};
ToolTip.defaultProps = {
	place: 'top',
	type: 'default',
	effect: 'solid',
	size: 'large',
	weight: 'regular'
};
